import { ChangeDetectionStrategy, Component, Input, OnInit, OnDestroy } from '@angular/core';
import { GrowProject } from '@inyova/grow';
import { AmplitudeService } from '@inyova/inyova-shared';
import { CustomerAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { filter, Observable, Subject, take } from 'rxjs';
import * as fromGrow from '../../grow.reducers';

@Component({
  selector: 'app-grow-projects-list',
  templateUrl: './grow-projects-list.component.html',
  styleUrls: ['./grow-projects-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GrowProjectsListComponent implements OnInit, OnDestroy {
  growProjects$: Observable<GrowProject[]>;
  growProjectsLoaded$: Observable<boolean>;
  viewProjectDetail: string;
  averageInterestRate: string;

  growProjectsCount = 0;

  @Input() growAccount: CustomerAccount;
  @Input() selectedProjects: number;

  protected readonly onDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.growProjects$ = this.store.pipe(select(fromGrow.selectInvestableGrowProjects));
    this.growProjectsLoaded$ = this.store.select(fromGrow.selectGrowProjectsLoaded);

    this.store
      .pipe(
        select(fromGrow.selectInvestableGrowProjects),
        filter((projects) => projects.length > 0),
        take(1)
      )
      .subscribe((projects) => {
        this.averageInterestRate = this.calculateInterestRate(projects);
        this.growProjectsCount = projects.length;
        this.amplitudeService.trackEvent('Project List Viewed', {
          number_projects_listed: projects.length,
          avg_interest_rate_projects_listed: this.averageInterestRate,
          number_projects_listed_investable: projects.length,
          placement: 'onboarding'
        });
      });

    this.viewProjectDetail = '';
  }

  showProjectDetails(project: GrowProject) {
    this.amplitudeService.trackEvent('Project Details Viewed', {
      project_name: project.title,
      intrest_rate: project.expected_return,
      maturity_date: new Date(project.end_date),
      project_id: project.id,
      minimum_investment: project.minimum_funding,
      currency: project.currency,
      investment_sector: project.sector,
      project_details_section_viewed: 'details',
      placement: 'onboarding'
    });
    this.viewProjectDetail = project.id;
  }

  ngOnDestroy(): void {
    this.amplitudeService.trackEvent('Project List Exited', {
      number_projects_selected: this.selectedProjects,
      number_projects_listed: this.growProjectsCount,
      avg_interest_rate_projects_listed: this.averageInterestRate,
      number_projects_listed_investable: this.growProjectsCount,
      placement: 'onboarding'
    });
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  hideProjectDetails() {
    this.viewProjectDetail = '';
  }

  calculateInterestRate(projects: GrowProject[]): string {
    const interestRates = projects.map((project) => project.expected_return);
    return ((interestRates.reduce((a, b) => a + b) / interestRates.length) * 100).toFixed(2);
  }
}
